import { useState } from "react";
import { toast } from "sonner";
import { Tv, Loader2, Play, Eye, Check, Copy, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";

const ST = {
  done: ["Configurado", "text-emerald-300 border-emerald-500/40 bg-emerald-500/10"],
  running: ["Configurando...", "text-sky-300 border-sky-500/40 bg-sky-500/10"],
  pending: ["Na fila", "text-sky-300 border-sky-500/40 bg-sky-500/10"],
  awaiting_verify: ["Aguardando usuário de acesso", "text-amber-300 border-amber-500/40 bg-amber-500/10"],
  needs_human: ["Precisa de atendente", "text-amber-300 border-amber-500/40 bg-amber-500/10"],
  failed: ["Falhou", "text-red-300 border-red-500/40 bg-red-500/10"],
};
const copy = (v, what) => navigator.clipboard.writeText(v || "").then(() => toast.success(`${what} copiado`));

const Field = ({ k, v, id }) => v ? (
  <div className="flex items-center gap-2">
    <span className="w-24 shrink-0 text-slate-500">{k}</span>
    <span data-testid={`app-config-${id}`} className="min-w-0 flex-1 truncate font-mono text-slate-200">{v}</span>
    <button onClick={() => copy(v, k)} className="p-1 text-slate-400 hover:text-white" title={`Copiar ${k}`}><Copy className="h-3.5 w-3.5" /></button>
  </div>
) : null;

const Item = ({ s, e, reload }) => {
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [label, cls] = ST[e.status] || [e.status, "text-slate-300 border-slate-500/40"];
  const call = async (fn) => { setBusy(true); try { await fn(); } catch (x) { toast.error(x.response?.data?.detail || "Falha"); } setBusy(false); reload(); };
  const run = () => (e.status !== "done" || window.confirm(`A lista já foi configurada no ${e.site_name}. Enviar de novo? Pode duplicar a lista WEPLAY no aparelho.`)) && call(async () => {
    const { data: r } = await api.post(`/chat/sessions/${s.id}/app-config/${e.id}/run`);
    r.ok ? toast.success("Lista configurada e cliente avisado") : toast.error(r.error || "Não foi possível configurar");
  });
  const done = () => call(() => api.post(`/chat/sessions/${s.id}/app-config/${e.id}/done`));
  const show = async () => { if (data) return setData(null); try { setData((await api.get(`/chat/sessions/${s.id}/app-config/${e.id}/data`)).data); } catch (x) { toast.error(x.response?.data?.detail || "Falha"); } };
  return (
    <div data-testid={`app-config-${e.id}`} className="rounded-lg border border-cyan-500/30 bg-cyan-500/[0.05] px-3 py-2 text-xs">
      <div className="flex items-center gap-1.5">
        <Tv className="h-3.5 w-3.5 shrink-0 text-cyan-300" />
        <span className="min-w-0 flex-1 truncate font-semibold text-cyan-100">{e.site_name}</span>
        <span data-testid="app-config-status" className={`shrink-0 rounded-md border px-1.5 py-0.5 text-[10px] font-semibold ${cls}`}>{label}</span>
      </div>
      <div className="mt-1 truncate font-mono text-[11px] text-slate-400">{e.kind === "ssiptv" ? e.device_key && `Código ${e.device_key}` : e.mac || (e.device_id && `ID ${e.device_id}`)}</div>
      <div>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {((e.kind === "xplayers" && e.mac) || (e.kind === "ssiptv" && e.device_key)) && e.status !== "running" && (
            <button data-testid="app-config-run" disabled={busy} onClick={run} className="inline-flex items-center gap-1 rounded-md bg-cyan-500 px-2.5 py-1 font-bold text-slate-950 hover:opacity-90 disabled:opacity-50">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />} {e.status === "done" ? "Configurar de novo" : "Configurar agora"}
            </button>)}
          <button data-testid="app-config-show" onClick={show} className="inline-flex items-center gap-1 rounded-md border border-[#1F293D] px-2 py-1 text-slate-300 hover:text-white"><Eye className="h-3.5 w-3.5" /> Dados</button>
          {e.status !== "done" && <button data-testid="app-config-done" disabled={busy} onClick={done} className="inline-flex items-center gap-1 rounded-md border border-emerald-500/40 px-2 py-1 text-emerald-300 hover:bg-emerald-500/10"><Check className="h-3.5 w-3.5" /> Feito</button>}
        </div>
      </div>
      {e.error && e.status !== "done" && <div data-testid="app-config-error" className="mt-1 text-[11px] text-amber-200/90">{e.error}</div>}
      {data && (
        <div data-testid="app-config-data" className="mt-2 space-y-1 rounded-md border border-[#1F293D] bg-[#0B1019] p-2">
          <Field k="MAC" v={data.mac} id="mac" />
          <Field k="Device ID" v={data.device_id} id="device-id" />
          <Field k="Device Key" v={data.device_key} id="device-key" />
          <Field k="Nome" v={data.playlist_name} id="name" />
          <Field k="URL M3U" v={data.m3u} id="m3u" />
          <Field k="PIN" v={data.pin} id="pin" />
          {data.notes && <div data-testid="app-config-notes" className="rounded border border-amber-500/30 bg-amber-500/[0.07] px-2 py-1 text-[11px] text-amber-100">{data.notes}</div>}
          <div className="pt-1 text-[11px] text-slate-500">EPG em branco · marcar "Protect Playlist" · nunca passar o PIN ao cliente.
            {data.upload_page && <a href={data.upload_page} target="_blank" rel="noreferrer" className="ml-2 inline-flex items-center gap-1 text-cyan-300 hover:underline">abrir site <ExternalLink className="h-3 w-3" /></a>}</div>
        </div>
      )}
    </div>
  );
};

export const AppConfigs = ({ s, reload }) => {
  const items = [...(s.app_configs || [])].reverse();
  if (!items.length) return null;
  return (
    <div data-testid="chat-app-configs" className="space-y-2">
      {items.map((e) => <Item key={e.id} s={s} e={e} reload={reload} />)}
    </div>
  );
};
